import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ScrollReveal';
import { fetchCollection, getVal } from '../lib/api';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadAboutData() {
      try {
        const [pageRes, valuesRes] = await Promise.all([
          fetchCollection('sider', { term: 'sidetype:om-os' }),
          fetchCollection('om_os_vaerdier', { sort: 'sort_order', order: 'asc' }),
        ]);

        if (pageRes.items && pageRes.items.length > 0) {
          setPageData(pageRes.items[0]);
        }
        if (valuesRes.items && valuesRes.items.length > 0) {
          setValues(valuesRes.items);
        }
      } catch (err) {
        console.error('Fejl ved indlæsning af Om os CMS data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadAboutData();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-stone-50">
      <section className="border-b border-stone-200 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <ScrollReveal animation="fade-in-down">
            <span className="text-xs font-bold uppercase tracking-[0.35em] text-brand-orange">Hvem er vi?</span>
            <h1 className="mt-3 text-4xl font-semibold text-slate-800 sm:text-5xl">
              {getVal(pageData?.title) || 'Om J F D Service'}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-500 sm:text-base">
              {getVal(pageData?.subtitle) || 'Læs historien om vores lille familievirksomhed i Ringe, og mød det dedikerede team bag den professionelle udlejning.'}
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="border-b border-stone-200 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            <ScrollReveal animation="fade-in-left" className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-[0.35em] text-brand-orange">Vores historie</span>
              <h2 className="text-3xl font-semibold leading-tight text-slate-800">
                {getVal(pageData?.title) || 'En lille familie virksomhed, som laver det på vores fridage.'}
              </h2>
              {pageData?.content ? (
                <div
                  className="space-y-4 text-base leading-relaxed text-slate-500"
                  dangerouslySetInnerHTML={{ __html: getVal(pageData.content) }}
                />
              ) : (
                <div className="space-y-4 text-base leading-relaxed text-slate-500">
                  <p>En lille familie virksomhed, som laver det på vores fridage. Vi har begge to fuldtids arbejde ved siden af.</p>
                  <p>Jannicke arbejder som paramediciner på døgn vagt.</p>
                  <p>Jan arbejder som Brandmand/Redningsdykker på døgn vagt.</p>
                </div>
              )}
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-orange-hover hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Kontakt os i dag <i className="fa-solid fa-arrow-right" />
                </Link>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {values.map((val, idx) => (
                <ScrollReveal
                  key={val.id || idx}
                  animation="fade-in-right"
                  delay={idx * 150}
                  className="rounded-[1.5rem] border border-stone-200 bg-white p-6 shadow-[0_12px_30px_rgba(148,163,184,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange">
                    <i className={`${val.icon || 'fa-solid fa-star'} text-lg`} />
                  </div>
                  <h3 className="mb-2 text-base font-semibold text-slate-800">{getVal(val.title)}</h3>
                  <p className="text-xs leading-relaxed text-slate-500">{getVal(val.description)}</p>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-stone-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <ScrollReveal animation="fade-in-down">
              <span className="text-xs font-bold uppercase tracking-[0.35em] text-brand-orange">Vores team</span>
              <h2 className="mt-3 text-3xl font-semibold text-slate-800">Mød vores team.</h2>
              <p className="mt-4 text-sm leading-7 text-slate-500">
                Her er familien bag J F D Service. Vi står klar til at hjælpe dig med din næste udlejning.
              </p>
            </ScrollReveal>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <ScrollReveal animation="fade-in-up" delay={0} className="group overflow-hidden rounded-[1.5rem] border border-stone-200 bg-white shadow-[0_12px_30px_rgba(148,163,184,0.08)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
              <div className="h-80 overflow-hidden bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/thumbnail-image4-1.jpg"
                  alt="Jan Faigh Danielsen"
                  className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-orange">Direktør</span>
                <h3 className="text-lg font-semibold text-slate-800">Jan Faigh Danielsen</h3>
              </div>
            </ScrollReveal>

            <ScrollReveal animation="fade-in-up" delay={150} className="group overflow-hidden rounded-[1.5rem] border border-stone-200 bg-white shadow-[0_12px_30px_rgba(148,163,184,0.08)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
              <div className="h-80 overflow-hidden bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/thumbnail-image5_1.jpg"
                  alt="Jannicke Faigh Danielsen"
                  className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-orange">Marketingchef</span>
                <h3 className="text-lg font-semibold text-slate-800">Jannicke Faigh Danielsen</h3>
              </div>
            </ScrollReveal>

            <ScrollReveal animation="fade-in-up" delay={300} className="group overflow-hidden rounded-[1.5rem] border border-stone-200 bg-white shadow-[0_12px_30px_rgba(148,163,184,0.08)] sm:col-span-2 lg:col-span-1 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
              <div className="h-80 overflow-hidden bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/assets/27b85900-9717-41d2-8622-634ea09330b6/lukas.jpg"
                  alt="Lukas Faigh Danielsen"
                  className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-orange">Kvalitetstester</span>
                <h3 className="text-lg font-semibold text-slate-800">Lukas Faigh Danielsen</h3>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

    </div>
  );
}
